import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Box, IconButton, Tooltip, TextField } from '@mui/material';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import CloseIcon from '@mui/icons-material/Close';

import {
  checkChangedFields,
  copyObjectWithoutRef,
  isDetails,
} from '@common/helpers/helpers';

import DetailsHeader from './DetailsHeader';
import styles from './TemplateDetails.module.scss';
import Footer from '@common/components/Footer/Footer';

const validateForm = (formData, t) => {
  const errors = {};
  let isValid = true;
  if (!formData?.name) {
    errors.name = t('required_field');
    isValid = false;
  }
  if (!formData?.type) {
    errors.type = t('required_field');
    isValid = false;
  }
  if (!formData?.dataType?.id) {
    errors.dataType = t('required_field');
    isValid = false;
  }

  return { isValid, errors };
};

const TemplateDetails = ({
  viewMode,
  details,
  onCancelGoBack,
  onSave,
  isLoading,
  onEditClick,
  onCopyClick,
  onRightDrawerClose,
}) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState(details);
  const [formErrors, setFormErrors] = useState({});
  const changedData = checkChangedFields(details, formData);

  const handleOnChange = (e) => {
    const fieldName = e.target.name;
    const value = e.target.value;

    const errorsTemp = copyObjectWithoutRef(formErrors);
    if (fieldName === 'dataType') {
    } else {
      delete errorsTemp[fieldName];
      setFormData((prevState) => ({
        ...prevState,
        [fieldName]: value,
      }));
    }
    setFormErrors(errorsTemp);
  };

  useEffect(() => {
    if (isDetails(viewMode)) {
      setFormData(details);
    }
  }, [viewMode, details]);

  return (
    <Box className={styles.wrap}>
      <DetailsHeader
        left={t('template_details')}
        right={
          <>
            <IconButton
              size='small'
              onClick={onRightDrawerClose}
              sx={{
                width: '60px',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                marginRight: 2,
                marginLeft: 2,
                color: 'primary.main',
              }}
            >
              <Tooltip title={t('close_button')}>
                <CloseIcon sx={{ transform: 'rotate(180deg)' }} />
              </Tooltip>
            </IconButton>

            <IconButton
              color='primary'
              disabled={!isDetails(viewMode) || !details}
              onClick={onCopyClick}
            >
              <Tooltip title={t('copy_template')}>
                <ContentCopyIcon />
              </Tooltip>
            </IconButton>
          </>
        }
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          width: '100%',
        }}
      >
        <Box
          sx={{
            zIndex: 1,
            px: 2,
            pt: 2,
            overflowY: 'auto',
            flex: 1,
          }}
        >
          <TextField
            name='name'
            label={t('template_name')}
            onChange={handleOnChange}
            value={formData?.name ?? ''}
            disabled={isDetails(viewMode)}
            size='small'
            sx={{
              mt: 1,
              height: '40px',
              '& .MuiFormHelperText-root': {
                mt: '0px',
              },
              width: '100%',
            }}
            error={formErrors?.name}
            helperText={formErrors?.name}
          />
          {/* <TextField
            label={t('template_type')}
            disabled={true}
            required
            value={getTemplateTypeValue(formData?.type)}
            size='small'
            sx={{
              mt: 4,
              height: '40px',
              '& .MuiFormHelperText-root': {
                mt: '0px',
              },
              width: '100%',
            }}
          /> */}
          <TextField
            label={t('template_type')}
            name='dataType'
            disabled={true}
            value={formData?.dataType?.name}
            error={formErrors?.dataType}
            size='small'
            sx={{
              mt: 4,
              height: '40px',
              '& .MuiFormHelperText-root': {
                mt: '0px',
              },
              width: '100%',
            }}
          />
          <TextField
            sx={{ mt: 4, width: '100%' }}
            label={t('description')}
            name='description'
            onChange={handleOnChange}
            value={formData?.description}
            disabled={isDetails(viewMode)}
            multiline
            size='small'
            rows={4}
          />
        </Box>

        <Footer
          onRightDrawerClose={onRightDrawerClose}
          viewMode={viewMode}
          onEditClick={onEditClick}
          onCancel={onCancelGoBack}
          isLoading={isLoading}
          isDisabled={!changedData}
          onSave={() => {
            const { isValid, errors } = validateForm(formData, t);

            if (isValid) {
              onSave(formData);
            } else {
              setFormErrors(errors);
            }
          }}
        />
      </Box>
    </Box>
  );
};

TemplateDetails.defaultProps = {
  header: '',
  details: null,
  viewMode: '',
};

TemplateDetails.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.arrayOf(PropTypes.object),
    ]),
  ).isRequired,
  details: PropTypes.shape({}),
  onSave: PropTypes.func.isRequired,
  header: PropTypes.string,
  onCancelGoBack: PropTypes.func.isRequired,
  viewMode: PropTypes.string,
};

export default TemplateDetails;
